import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';

interface AchievementsState {
    claimingReward: boolean;
    claimedRewards: Array<{ achievementId: string; level: number; timestamp: string; }>;
    error: string | null;
}

const initialState: AchievementsState = {
    claimingReward: false,
    claimedRewards: [],
    error: null
};

export const claimAchievementReward = createAsyncThunk(
    'achievements/claimReward',
    async (
        { achievementId, level }: { achievementId: string; level: number; },
        { rejectWithValue, getState }
    ) => {
        try {
            const state: RootState = getState() as RootState;
            const accessToken = state.authentication.access_token;

            if (!accessToken) {
                return rejectWithValue({ error: 'No access token available' });
            }

            const response = await fetch(
                `${window.config.playerApiUrl}/player/data/player-achievements/${achievementId}/levels/${level}/claim-reward`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return { achievementId, level, data };
        } catch (err: any) {
            return rejectWithValue({ error: err.toString() });
        }
    }
);

const achievementsSlice = createSlice({
    name: 'achievements',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(claimAchievementReward.pending, (state) => {
                state.claimingReward = true;
                state.error = null;
            })
            .addCase(claimAchievementReward.fulfilled, (state, action) => {
                state.claimingReward = false;
                state.claimedRewards.push({
                    achievementId: action.payload.achievementId,
                    level: action.payload.level,
                    timestamp: new Date().toISOString()
                });
            })
            .addCase(claimAchievementReward.rejected, (state, action) => {
                state.claimingReward = false;
                state.error = action.payload as string || 'Failed to claim reward';
            });
    }
});

export const { clearError } = achievementsSlice.actions;

// Selectors
export const selectClaimingReward = (state: { achievements: AchievementsState; }) => state.achievements.claimingReward;
export const selectClaimedRewards = (state: { achievements: AchievementsState; }) => state.achievements.claimedRewards;
export const selectAchievementsError = (state: { achievements: AchievementsState; }) => state.achievements.error;

export default achievementsSlice.reducer;
